/**
 * OAuth Callback Page for Rider Gateway
 *
 * Handles the OAuth redirect. Renders one of three states — Processing,
 * Success (very brief), or Error (with auto-redirect) — using the rider's
 * lime/midnight palette via the shared auth layout.
 */

import type { FC } from 'react';
import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useAppDispatch, useAppSelector } from '../hooks/reduxHooks';
import {
  handleAuthCallback,
  selectAuthError,
  selectIsAuthenticated,
  selectIsLoading,
} from '../store/slices/authSlice';
import MobileAuthLayout from '../components/templates/MobileAuthLayout';
import { r } from '../theme/riderTheme';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Card = styled.section`
  width: 100%;
  max-width: 380px;
  padding: 28px 24px 24px;
  background: ${({ theme }) => r(theme).surface.sheet};
  color: ${({ theme }) => r(theme).text.primary};
  border-radius: 20px;
  box-shadow:
    0 1px 0 ${({ theme }) => r(theme).surface.edge},
    0 20px 60px -20px rgba(0, 0, 0, 0.45);
  text-align: center;
  animation: ${fadeIn} 380ms cubic-bezier(0.2, 0.8, 0.2, 1);
`;

const Eyebrow = styled.div`
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.faint};
  margin-bottom: 14px;
`;

const Title = styled.h1`
  margin: 0 0 8px;
  font-family: 'Geist', sans-serif;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1.1;
  color: ${({ theme }) => r(theme).text.primary};
`;

const Body = styled.p`
  margin: 0 0 18px;
  font-family: 'Geist', sans-serif;
  font-size: 13.5px;
  line-height: 1.55;
  color: ${({ theme }) => r(theme).text.muted};
`;

const IndicatorRing = styled.div<{ $variant: 'loading' | 'success' | 'error' }>`
  margin: 8px auto 18px;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  background: ${({ $variant, theme }) => {
    const t = r(theme);
    switch ($variant) {
      case 'success':
        return t.surface.raisedSelected;
      case 'error':
        return 'rgba(255, 25, 35, 0.06)';
      case 'loading':
      default:
        return t.surface.inputIdle;
    }
  }};
  border: 1px solid
    ${({ $variant, theme }) => {
      const t = r(theme);
      switch ($variant) {
        case 'success':
          return t.accent.limeDeep;
        case 'error':
          return 'rgba(255, 25, 35, 0.32)';
        case 'loading':
        default:
          return t.surface.edge;
      }
    }};
`;

const RingSpinner = styled.span`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid ${({ theme }) => r(theme).surface.edgeStrong};
  border-top-color: ${({ theme }) => r(theme).accent.lime};
  animation: spin 720ms linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const StatusGlyph = styled.span<{ $variant: 'success' | 'error' }>`
  font-family: 'Geist', sans-serif;
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
  color: ${({ $variant, theme }) =>
    $variant === 'success' ? r(theme).accent.ok : r(theme).accent.alert};
`;

const ErrorMessage = styled.div`
  margin: 0 auto 16px;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(255, 25, 35, 0.05);
  border: 1px solid rgba(255, 25, 35, 0.32);
  color: ${({ theme }) => r(theme).accent.alert};
  font-family: 'Geist', sans-serif;
  font-size: 12px;
  line-height: 1.45;
  text-align: left;
  max-width: 100%;
  overflow-wrap: anywhere;
`;

const RedirectNote = styled.div`
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.faint};
  margin-top: 12px;
`;

const AuthCallbackPage: FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const isLoading = useAppSelector(selectIsLoading);
  const authError = useAppSelector(selectAuthError);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        if (import.meta.env.VITE_USE_MOCK_AUTH === 'true') {
          navigate('/dashboard', { replace: true });
          return;
        }

        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        if (error) {
          navigate('/login', {
            replace: true,
            state: { error: errorDescription || 'Authentication failed' },
          });
          return;
        }

        const code = searchParams.get('code');
        if (!code) {
          navigate('/login', {
            replace: true,
            state: { error: 'No authorization code received' },
          });
          return;
        }

        await dispatch(handleAuthCallback(searchParams));
      } catch (error) {
        console.error('Callback handling error:', error);
        navigate('/login', {
          replace: true,
          state: { error: 'Failed to process authentication callback' },
        });
      }
    };

    handleCallback();
  }, [dispatch, navigate, searchParams]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (authError && !isLoading) {
      const timer = setTimeout(() => {
        navigate('/login', {
          replace: true,
          state: { error: authError.message || 'Authentication failed' },
        });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [authError, isLoading, navigate]);

  return (
    <MobileAuthLayout>
      <Card>
        <Eyebrow>Auth · Callback</Eyebrow>

        {isLoading && !authError && (
          <>
            <IndicatorRing $variant="loading" aria-hidden>
              <RingSpinner />
            </IndicatorRing>
            <Title>Processing authentication</Title>
            <Body>Hang on — we're verifying your credentials.</Body>
          </>
        )}

        {authError && (
          <>
            <IndicatorRing $variant="error" aria-hidden>
              <StatusGlyph $variant="error">!</StatusGlyph>
            </IndicatorRing>
            <Title>Authentication failed</Title>
            <ErrorMessage>{authError.message || 'Authentication failed'}</ErrorMessage>
            <RedirectNote>Redirecting to sign in…</RedirectNote>
          </>
        )}

        {isAuthenticated && (
          <>
            <IndicatorRing $variant="success" aria-hidden>
              <StatusGlyph $variant="success">✓</StatusGlyph>
            </IndicatorRing>
            <Title>You're in</Title>
            <Body>Welcome back. Redirecting to your dashboard…</Body>
          </>
        )}
      </Card>
    </MobileAuthLayout>
  );
};

export default AuthCallbackPage;
